import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Image,
  useToast,
  Center,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const ProfileTab = () => {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    nickname: "",
    img_profile: "",
    phone_number: "",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [imageError, setImageError] = useState(false);

  // 사용자 정보 불러오기
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data, error } = await supabase
          .from("user")
          .select("name, nickname, img_profile, phone_number")
          .eq("email", user.email)
          .single();

        if (error) throw error;

        setFormData({
          name: data.name || "",
          nickname: data.nickname || "",
          img_profile: data.img_profile || "",
          phone_number: data.phone_number || "",
        });
        setPreviewImage(data.img_profile || "");
        setImageError(false);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        toast({
          title: "프로필 정보를 불러오는데 실패했습니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    if (user) {
      fetchUserProfile();
    }
  }, [user]);

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 이미지 미리보기만 생성
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
      setImageError(false);
    };
    reader.readAsDataURL(file);

    // 선택된 파일 저장
    setSelectedFile(file);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      let newImgProfile = formData.img_profile;

      // 새로운 이미지가 선택된 경우에만 업로드
      if (selectedFile) {
        const fileExt = selectedFile.name.split(".").pop();
        const fileName = `${user.email}-${Date.now()}.${fileExt}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("profile-images")
          .upload(fileName, selectedFile);

        if (uploadError) throw uploadError;

        const {
          data: { publicUrl },
        } = supabase.storage.from("profile-images").getPublicUrl(fileName);

        newImgProfile = publicUrl;
      }

      // user 테이블 업데이트
      const { error: updateError } = await supabase
        .from("user")
        .update({
          name: formData.name,
          nickname: formData.nickname,
          img_profile: newImgProfile,
          phone_number: formData.phone_number,
        })
        .eq("email", user.email);

      if (updateError) throw updateError;

      // 업데이트 성공 시 formData 업데이트
      setFormData((prev) => ({
        ...prev,
        img_profile: newImgProfile,
      }));

      // 선택된 파일 초기화
      setSelectedFile(null);

      toast({
        title: "프로필이 성공적으로 업데이트되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      // 홈으로 이동
      navigate("/");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "프로필 업데이트 실패",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Center>
          <Box position="relative" cursor="pointer" onClick={handleImageClick}>
            {previewImage && !imageError ? (
              <Image
                src={previewImage}
                alt="Profile"
                boxSize="200px"
                objectFit="cover"
                borderRadius="full"
                onError={() => setImageError(true)}
              />
            ) : (
              <Box
                w="200px"
                h="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="gray.100"
                borderRadius="full"
                fontWeight="bold"
                color="gray.400"
                fontSize="xl"
                mx="auto"
              >
                이미지 없음
              </Box>
            )}
            <Input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              display="none"
              onChange={handleImageChange}
            />
          </Box>
        </Center>

        <FormControl>
          <FormLabel>이름</FormLabel>
          <Input
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="이름을 입력하세요"
          />
        </FormControl>

        <FormControl>
          <FormLabel>활동명</FormLabel>
          <Input
            name="nickname"
            value={formData.nickname}
            onChange={handleInputChange}
            placeholder="활동명을 입력하세요"
          />
        </FormControl>

        <FormControl>
          <FormLabel>전화번호(- 없이)</FormLabel>
          <Input
            name="phone_number"
            value={formData.phone_number}
            onChange={handleInputChange}
            placeholder="전화번호를 입력하세요(- 없이)"
          />
        </FormControl>

        <Button
          colorScheme="blue"
          size="lg"
          onClick={handleSubmit}
          isLoading={isLoading}
        >
          수정하기
        </Button>
      </VStack>
    </Box>
  );
};

export default ProfileTab;
